import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { HERO, PROF_ORDER } from "./data";
import { useReducedMotion } from "../../hooks/useMotion";
import { useT } from "./i18n";

export default function Hero() {
  const { t } = useT();
  const imgRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    const on = () => {
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 700);
        if (imgRef.current) imgRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.06)`;
      });
    };
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); cancelAnimationFrame(raf); };
  }, [reduced]);

  return (
    <section className="relative min-h-[92vh] overflow-hidden">
      <img ref={imgRef} src={HERO} alt="A groom working calmly with a horse at the stable"
        className="absolute inset-0 h-full w-full object-cover" style={{ transform: "scale(1.06)" }} fetchpriority="high" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#173A2C]/85 via-[#173A2C]/55 to-[#173A2C]/85" />
      <div className="grain absolute inset-0 opacity-15" />

      <div className="relative mx-auto flex min-h-[92vh] max-w-6xl flex-col justify-center px-5 pt-28 pb-16">
        <div className="max-w-2xl">
          <span data-testid="hero-badge" className="inline-block rounded-full border border-white/25 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-white/90">
            {t.hero.badge}
          </span>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[1.02] text-white sm:text-6xl lg:text-7xl">
            {t.hero.h1a} <span className="text-[#D8BE8A]">{t.hero.h1b}</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">{t.hero.subtitle}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link to="/register" data-testid="hero-join-community-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C4A46B] px-7 py-3.5 text-sm font-semibold text-[#173A2C] transition-transform hover:-translate-y-0.5">
              {t.hero.joinCta} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/register" data-testid="hero-hire-talent-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              {t.hero.hireCta}
            </Link>
          </div>

          <div data-testid="hero-profession-selector" className="mt-8 flex flex-wrap gap-2">
            {PROF_ORDER.map((id) => (
              <a key={id} href="#professions" data-testid={`hero-prof-${id}`}
                className="rounded-full border border-white/25 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/85 transition-colors hover:border-[#C4A46B] hover:text-white">
                {t.prof.cards[id].label}
              </a>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {t.hero.trust.map((x) => (
              <span key={x} className="inline-flex items-center gap-1.5 text-xs text-white/80">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#C4A46B]" /> {x}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
